import { inject, Injectable } from '@angular/core';
import type { Auth0Client } from '@auth0/auth0-spa-js';
import { environment } from '@environments/environment';
import { LogsService } from '@services/logs';
import { RoutesPath } from '@shared/constants';

interface AppState {
  target: string;
}

/** Browser only: the Auth0 SDK needs `window.crypto`, which the SSG prerender does not have. */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  readonly #logsService = inject(LogsService);
  #client?: Promise<Auth0Client>;

  readonly isAvailable = !!environment.AUTH0;

  /** Leaves the site for the Auth0 page, then comes back through the callback and ends on `target`. */
  async loginWithRedirect(target: string, signup = false): Promise<void> {
    const client = await this.#getClient();
    await client.loginWithRedirect<AppState>({
      appState: { target },
      authorizationParams: signup ? { screen_hint: 'signup' } : {},
    });
  }

  /** Loads the SDK whenever login is available, even for a visitor who never logged in. */
  async isAuthenticated(): Promise<boolean> {
    return this.isAvailable && (await this.#getClient()).isAuthenticated();
  }

  /** Never resolves, so the app never renders on the callback page it is about to leave. */
  async handleRedirectCallback(): Promise<never> {
    let target = '/';
    if (this.isAvailable && new URLSearchParams(location.search).has('state')) {
      try {
        const { appState } = await (await this.#getClient()).handleRedirectCallback<AppState>();
        target = appState?.target ?? target;
      } catch (error) {
        this.#logsService.error('AuthenticationService', 'Auth0 redirect callback failed', error);
      }
    }
    location.replace(target);
    return new Promise<never>(() => undefined);
  }

  // Loaded on demand: the SDK weighs 63 KB gzipped, which only visitors who log in should pay.
  #getClient(): Promise<Auth0Client> {
    this.#client ??= import('@auth0/auth0-spa-js').then(({ Auth0Client }) => {
      const config = environment.AUTH0;
      if (!config) {
        throw new Error('Auth0 is not configured for this environment');
      }
      return new Auth0Client({
        domain: config.domain,
        clientId: config.clientId,
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        // The default in-memory cache would lose the session as soon as the visitor leaves for the espace client.
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: `${location.origin}/${RoutesPath.AUTH_CALLBACK}`,
          audience: config.audience,
          ui_locales: 'fr',
        },
      });
    });
    return this.#client;
  }
}
